
import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../services/subscriptionService';
import { storage } from '../lib/storage';
import { SubscriptionBox, SubscriptionFrequency, Product } from '../types';
import { 
  Package, 
  Plus, 
  Trash2, 
  Calendar, 
  Users, 
  Zap, 
  Clock, 
  Loader2, 
  X, 
  Check, 
  AlertCircle,
  LayoutGrid,
  TrendingUp,
  Award,
  Sparkles
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminSubBoxes: React.FC<Props> = ({ storeId }) => {
  const [boxes, setBoxes] = useState<SubscriptionBox[]>([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 49,
    frequency: 'MONTHLY' as SubscriptionFrequency,
    items: [] as string[],
    imageUrl: '',
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setBoxes(subscriptionService.getStoreBoxes(storeId));
    setSubscriberCount(subscriptionService.getStoreSubscribersCount(storeId));
    setProducts(storage.getProducts().filter(p => p.storeId === storeId));
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    subscriptionService.createBox({ ...formData, storeId });
    setShowModal(false);
    loadData();
    setFormData({ name: '', description: '', price: 49, frequency: 'MONTHLY', items: [], imageUrl: '', isActive: true });
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Recurring Revenue Hub</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Subscription Box Engine</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Create New Box
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center"><Users className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Active Subscribers</p>
               <h3 className="text-3xl font-black text-gray-900">{subscriberCount}</h3>
            </div>
         </div>
         <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl flex items-center gap-6">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center"><TrendingUp className="h-7 w-7" /></div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Est. Monthly Rev</p>
               <h3 className="text-3xl font-black text-gray-900">${(subscriberCount * 49).toLocaleString()}</h3>
            </div>
         </div>
         <div className="bg-gray-900 p-8 rounded-[32px] shadow-2xl flex items-center gap-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><Award className="h-16 w-16" /></div>
            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-blue-400"><Zap className="h-7 w-7" /></div>
            <div className="relative">
               <p className="text-blue-300 text-[10px] font-black uppercase tracking-widest">SaaS Tier Status</p>
               <h3 className="text-xl font-black italic">Boutique Pro</h3>
            </div>
         </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {boxes.length === 0 ? (
              <div className="bg-white p-24 text-center space-y-4 rounded-[40px] border border-dashed border-gray-200 opacity-50">
                 <Package className="h-16 w-16 mx-auto text-gray-300" />
                 <p className="font-black uppercase tracking-widest text-xs">No active subscription boxes yet. Launch one to fix your revenue!</p>
              </div>
            ) : (
              boxes.map(box => (
                <div key={box.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex flex-col md:flex-row justify-between items-center gap-6 group hover:border-blue-200 transition-all">
                   <div className="flex items-center gap-6">
                      <div className="w-20 h-20 rounded-3xl overflow-hidden bg-gray-50 border border-gray-100">
                         <img src={box.imageUrl} className="w-full h-full object-cover" alt="" />
                      </div>
                      <div>
                         <div className="flex items-center gap-3">
                            <h3 className="text-xl font-black text-gray-900">{box.name}</h3>
                            <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${box.frequency === 'MONTHLY' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-purple-50 text-purple-600 border-purple-100'}`}>
                               {box.frequency}
                            </span>
                         </div>
                         <p className="text-xs text-gray-400 font-medium mt-1 leading-relaxed max-w-md line-clamp-1">{box.description}</p>
                         <div className="flex items-center gap-4 mt-3">
                            <span className="flex items-center gap-1.5 text-[10px] font-black text-gray-900 uppercase tracking-widest">Price: ${box.price}</span>
                            <span className="flex items-center gap-1.5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Includes {box.items.length} items</span>
                         </div>
                      </div>
                   </div>
                   <div className="flex items-center gap-3">
                      <button className="p-4 bg-gray-50 text-gray-400 hover:text-red-600 rounded-2xl transition-all"><Trash2 className="h-5 w-5" /></button>
                   </div>
                </div>
              ))
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-blue-600 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Sparkles className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">AI Sub-Insight</h3>
                  <p className="text-blue-100 text-sm leading-relaxed">
                     Bhai, data shows users love "The Gamer Box". Try adding a random $5 accessory every 3rd month to reduce churn by 12%.
                  </p>
                  <button className="w-full py-4 bg-white text-blue-600 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl">
                     Optimize My Retention
                  </button>
               </div>
            </div>
         </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">Launch Subscription Box</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-10 space-y-8 max-h-[70vh] overflow-y-auto">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Box Name</label>
                    <input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Essential Tech Crate" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Price / Cycle ($)</label>
                       <input type="number" value={formData.price} onChange={e => setFormData({...formData, price: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Frequency</label>
                       <select value={formData.frequency} onChange={e => setFormData({...formData, frequency: e.target.value as any})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold">
                          <option value="MONTHLY">Monthly Billing</option>
                          <option value="QUARTERLY">Quarterly Billing</option>
                       </select>
                    </div>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Image URL</label>
                    <input value={formData.imageUrl} onChange={e => setFormData({...formData, imageUrl: e.target.value})} placeholder="https://..." className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</label>
                    <textarea rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold resize-none" />
                 </div>
                 <button type="submit" className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2">
                    Activate Box <Zap className="h-5 w-5 fill-current" />
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubBoxes;
